/**
 * Enhances the server-rendered, cart-aware PDP "Recommended add-ons" bundle
 * (product-complementary.liquid + pdp-complementary-card.liquid): one "Add N to cart"
 * button adds all selected add-ons in one atomic /cart/add.js call (guarded, surfaces
 * errors) then opens the drawer; checkboxes (when 2+) toggle; "In cart" is server-seeded.
 */
class PdpComplementaryAddons extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()
    this._cartToken = 0
    this._submitting = false
    this._statusReady = false
    this._skipNextRefresh = false
    // Seed in-cart variant ids from the server (it already read `cart`) — no /cart.js on load.
    const vids = (this.dataset.cartVariantIds || '').split(',').map(s => s.trim()).filter(Boolean)
    this._inCartVariantIds = new Set(vids)
    this._addText = this.dataset.addText || 'Add [count] to cart'
    this._addNoneText = this.dataset.addNoneText || 'Add to cart'
    this._errorText = this.dataset.errorText || 'Could not add to cart — please try again.'
    this._bundleTotalEl = this.querySelector('[data-bundle-total]')
    this._bundleLabelEl = this.querySelector('[data-bundle-label]')
    this._bundleStatusEl = this.querySelector('[data-bundle-status]')
    this._bundleErrorEl = this.querySelector('[data-bundle-error]')
    this._bundleButton = this.querySelector('[data-bundle-add]')
    this._bundleEl = this.querySelector('[data-bundle]')
    this._cards = Array.from(this.querySelectorAll('.pdp-complementary-card'))
    this._bindEvents()
    this._initVariants()
    this._recalcBundle()
    this._statusReady = true
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort()
  }

  _bindEvents() {
    const opts = { signal: this.abortController.signal }
    if (this._bundleButton) this._bundleButton.addEventListener('click', this._addAll.bind(this), opts)
    this.querySelectorAll('.pdp-complementary-card__select-input').forEach(box => {
      box.addEventListener('change', () => this._recalcBundle(), opts)
    })
    document.addEventListener('ajaxProduct:added', this._onProductAdded.bind(this), opts)
    document.addEventListener('cart:updated', this._onCartUpdated.bind(this), opts)
  }

  // Our own bundle add applies optimistically (_afterBundleAdd), so skip that one refetch.
  _onProductAdded() {
    if (this._skipNextRefresh) { this._skipNextRefresh = false; return }
    this._refreshCartState()
  }

  _onCartUpdated(event) {
    const cart = event && event.detail && event.detail.cart
    if (cart && Array.isArray(cart.items)) this._applyCartState(cart.items)
    else this._refreshCartState()
  }

  // Variant change: sync price + the total's cents, re-evaluate in-cart at the VARIANT
  // level (another variant may not be in the cart), recompute the bundle.
  _initVariants() {
    const opts = { signal: this.abortController.signal }
    this._cards.forEach(card => {
      const select = card.querySelector('.pdp-complementary-card__variant')
      if (!select) return
      select.addEventListener('change', () => {
        const opt = select.options[select.selectedIndex]
        this._setCardPrice(card, opt)
        if (opt && opt.getAttribute('data-cents')) card.dataset.selectedCents = opt.getAttribute('data-cents')
        this._evaluateCard(card)
        this._recalcBundle()
      }, opts)
    })
  }

  _setCardPrice(card, opt) {
    const priceEl = card.querySelector('.pdp-complementary-card__price')
    const price = opt && opt.getAttribute('data-price')
    if (!priceEl || !price) return
    const compare = opt.getAttribute('data-compare')
    priceEl.innerHTML = compare
      ? '<span class="pdp-complementary-card__price-sale">' + price + '</span> '
        + '<s class="pdp-complementary-card__price-was">' + compare + '</s>'
      : price
  }

  _recalcBundle() {
    const addable = this._cards.some(card => !card.classList.contains('is-in-cart'))
    if (this._bundleEl) this._bundleEl.hidden = !addable
    const selected = this._selectedCards()
    let cents = 0
    selected.forEach(card => { cents += parseInt(card.dataset.selectedCents, 10) || 0 })
    const total = (window.theme && theme.Currency) ? theme.Currency.formatMoney(cents, theme.Currency.productPriceFormat()) : ''
    if (this._bundleTotalEl && total) this._bundleTotalEl.innerHTML = total
    if (this._bundleButton) this._bundleButton.disabled = selected.length === 0
    const label = this._bundleLabel(selected.length)
    if (this._bundleLabelEl) this._bundleLabelEl.textContent = label
    // Announce to SR users on user-driven change only (not the initial/hydration render).
    const status = total ? label + ' — total ' + total : label
    if (this._bundleStatusEl && this._statusReady && this._bundleStatusEl.textContent !== status) {
      this._bundleStatusEl.textContent = status
    }
  }

  _bundleLabel(count) {
    return count === 0 ? this._addNoneText : this._addText.replace('[count]', count)
  }

  // In the bundle when a variant is chosen (non-empty), not in-cart, and checked/checkbox-less.
  _selectedCards() {
    return this._cards.filter(card => {
      if (card.classList.contains('is-in-cart') || !this._variantId(card)) return false
      const box = card.querySelector('.pdp-complementary-card__select-input')
      return !box || box.checked
    })
  }

  _addAll() {
    if (this._submitting) return
    const items = this._selectedCards().map(card => {
      const id = this._variantId(card)
      return id ? { id: id, quantity: 1 } : null
    }).filter(Boolean)
    if (items.length) this._submitBundle(items)
  }

  _variantId(card) {
    const select = card.querySelector('.pdp-complementary-card__variant')
    return select ? select.value : card.dataset.variantId
  }

  _submitBundle(items) {
    this._submitting = true
    this._setError('')
    if (this._bundleButton) { this._bundleButton.disabled = true; this._bundleButton.classList.add('btn--loading') }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items }),
      credentials: 'same-origin',
    })
      .then(r => r.json().then(data => ({ ok: r.ok, data: data })))
      .then(result => this._finishSubmit(result.ok, result.data))
      .catch(() => this._finishSubmit(false, null))
  }

  _finishSubmit(ok, data) {
    this._submitting = false
    if (this._bundleButton) this._bundleButton.classList.remove('btn--loading')
    if (ok) this._afterBundleAdd(data)
    else this._setError((data && (data.description || data.message)) || this._errorText)
    this._recalcBundle()
  }

  _setError(msg) {
    if (this._bundleErrorEl) this._bundleErrorEl.textContent = msg || ''
  }

  // Open + rebuild the drawer via ajaxProduct:added (or navigate to the cart on a cart-page
  // store). The atomic add lets us reflect the new variants instantly and skip our own
  // refetch — no extra /cart.js round-trip.
  _afterBundleAdd(data) {
    if (window.theme && theme.settings && theme.settings.cartType === 'page') {
      window.location.href = (theme.routes && theme.routes.cartPage) || '/cart'
      return
    }
    const items = (data && data.items) || []
    items.forEach(i => { const vid = i && (i.variant_id || i.id); if (vid) this._inCartVariantIds.add(String(vid)) })
    this._cards.forEach(card => this._evaluateCard(card))
    this._recalcBundle()
    this._skipNextRefresh = true
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: items[0] || {} } }))
  }

  _refreshCartState() {
    const token = ++this._cartToken
    fetch('/cart.js', { credentials: 'same-origin', signal: this.abortController.signal })
      .then(r => r.json())
      .then(cart => {
        if (token !== this._cartToken) return
        if (cart && Array.isArray(cart.items)) this._applyCartState(cart.items)
      })
      .catch(() => {})
  }

  _applyCartState(items) {
    this._inCartVariantIds = new Set(items.map(i => String(i.variant_id || i.id)))
    this._cards.forEach(card => this._evaluateCard(card))
    this._recalcBundle()
  }

  _evaluateCard(card) {
    const inCart = this._inCartVariantIds.has(String(this._variantId(card)))
    card.classList.toggle('is-in-cart', inCart)
    const box = card.querySelector('.pdp-complementary-card__select-input')
    if (box) box.disabled = inCart
  }
}

customElements.define('pdp-complementary-addons', PdpComplementaryAddons)
